import React, { useEffect, useState } from 'react'
import {
  CCol, CRow, CAlert, CForm, CFormInput, CFormLabel, CFormSelect, CButton, CSpinner,
  CBadge, CTabs, CTabList, CTab, CTabPanel,
} from '@coreui/react'
import { api, currentUser } from '../api/client'
import WwGrid, { WwColumn } from '../components/ww/WwGrid'

const columnas: WwColumn[] = [
  { key: 'auditoriaFecha', title: 'Fecha', type: 'date' },
  { key: 'auditoriaUsuario', title: 'Usuario' },
  { key: 'auditoriaHora', title: 'Hora' },
  { key: 'auditoriaPrograma', title: 'Programa' },
  { key: 'auditoriaEntro', title: 'Entro por' },
  {
    key: 'auditoriaAccion',
    title: 'Acción',
    // [RGM] 2026-10-16 (pedido Rafael): badge de acción igual que la pestaña
    // Actividad. Valores de sesión del GX (login.cs:1095) → Entró/Salió.
    render: (row) => {
      const accion = String(row.auditoriaAccion ?? '')
      return <CBadge color={COLOR_ACCION_GX[accion] ?? 'secondary'}>{accion}</CBadge>
    },
  },
]

// [RGM] 2026-10-16 (aprobado Rafael — auditoría de actividad): visor de la
// tabla NUEVA AuditoriaEvento (quién crea/modifica/elimina con hora, módulo,
// entidad y detalle). Las SESIONES (Entró/Salió) NO están acá: viven SOLO en
// la tabla GX Auditoria (pestaña "Auditoria" de esta misma página — división
// de responsabilidades [RGM] 2026-10-16). Contratos del backend:
//  - SIEMPRE scoped por la empresa del claim — 403 sin empresa
//    (auditoriacontroller.cs:121-123); el filtro "hasta" INCLUYE el día
//    completo y usuario/módulo/acción son EXACTOS, fecha desc, Take 500
//    (auditoriaeventoservice.cs:201-224).
//  - Acciones/orígenes: auditoriaevento.cs:28-43; módulos: :45-52.
//  - Retención: mes en curso de los eventos (la poda es global al registrar);
//    las sesiones de la tabla GX se conservan 3 meses (poda global).
interface ActividadEvento {
  auditoriaEventoNro: number
  empresaCodigo: string
  auditoriaEventoFecha: string
  auditoriaEventoUsuario: string
  auditoriaEventoNombre: string
  auditoriaEventoRol: number
  auditoriaEventoAccion: string
  auditoriaEventoModulo: string
  auditoriaEventoEntidad: string
  auditoriaEventoDetalle: string
  auditoriaEventoOrigen: string
}

// Color del badge por acción (pestaña Actividad: solo crear/modificar/eliminar;
// las entradas/salidas viven en la tabla GX).
const COLOR_ACCION: Record<string, string> = {
  CREAR: 'primary',
  MODIFICAR: 'warning',
  ELIMINAR: 'danger',
}

// Badge de la pestaña GX (sesiones): Entró=verde, Salió=gris (mismo criterio
// visual que la pestaña Actividad; valores del GX login.cs:1095).
const COLOR_ACCION_GX: Record<string, string> = {
  'Entró': 'success',
  'Salió': 'secondary',
}

/** Fecha y hora LOCAL del evento (dd/MM/yyyy HH:mm) — el backend envía el
 *  timestamp ISO en auditoriaEventoFecha. */
function fmtFechaHora(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  const f = (n: number) => String(n).padStart(2, '0')
  return `${f(d.getDate())}/${f(d.getMonth() + 1)}/${d.getFullYear()} ${f(d.getHours())}:${f(d.getMinutes())}`
}

/** Texto PLANO del usuario para el export: nombre + código (cae al código si
 *  no hay nombre — el backend lo resuelve al registrar, :284-298). */
function fmtUsuario(row: any): string {
  const nombre = String(row.auditoriaEventoNombre ?? '')
  const codigo = String(row.auditoriaEventoUsuario ?? '')
  if (nombre && codigo) return `${nombre} (${codigo})`
  return nombre || codigo
}

const columnasActividad: WwColumn[] = [
  {
    key: 'auditoriaEventoFecha',
    title: 'Fecha y hora',
    type: 'date',
    width: '11rem',
    render: (row) => fmtFechaHora(String(row.auditoriaEventoFecha ?? '')),
    exportValue: (row) => fmtFechaHora(String(row.auditoriaEventoFecha ?? '')),
  },
  {
    key: 'auditoriaEventoUsuario',
    title: 'Usuario',
    width: '15rem',
    render: (row) => (
      <span>
        {row.auditoriaEventoNombre || row.auditoriaEventoUsuario}
        {row.auditoriaEventoNombre && row.auditoriaEventoUsuario && (
          <small className="text-body-secondary ms-1">({row.auditoriaEventoUsuario})</small>
        )}
      </span>
    ),
    exportValue: (row) => fmtUsuario(row),
  },
  {
    key: 'auditoriaEventoAccion',
    title: 'Acción',
    width: '8rem',
    render: (row) => {
      const accion = String(row.auditoriaEventoAccion ?? '')
      return <CBadge color={COLOR_ACCION[accion] ?? 'secondary'}>{accion}</CBadge>
    },
  },
  { key: 'auditoriaEventoModulo', title: 'Módulo', width: '7rem' },
  { key: 'auditoriaEventoEntidad', title: 'Entidad', width: '11rem' },
  { key: 'auditoriaEventoOrigen', title: 'Origen', width: '6rem' },
  {
    key: 'auditoriaEventoDetalle',
    title: 'Detalle',
    // Texto LARGO con wrap: la grilla va en una línea por celda y el div
    // recupera el salto de línea solo en esta columna.
    render: (row) => (
      <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
        {row.auditoriaEventoDetalle}
      </div>
    ),
  },
]

// Auditoría — réplica de auditoriageneral.cs:57 (auditoriageneral GX).
// El GX SÍ permite Modificar/Eliminar (auditoriageneral.cs:590, 1392-1395;
// auditoriaww.cs:2248-2258 'DO UPDATE'/'DO DELETE'; BC auditoria_bc.cs:377-471)
// y el backend conserva ese contrato (PUT/DELETE /auditoria), pero la VISTA
// quedó SOLO LECTURA por pedido de Rafael [RGM] 2026-10-16: sin columna
// Acciones (igual que la pestaña Actividad) — una auditoría no se edita.
const AuditoriaPage: React.FC = () => {
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [rows, setRows] = useState<any[]>([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')

  // ── Visor de ACTIVIDAD (tab "Actividad") ───────────────────────────────
  const [tabActiva, setTabActiva] = useState('gx')
  const [eventos, setEventos] = useState<ActividadEvento[]>([])
  const [cargandoActividad, setCargandoActividad] = useState(true)
  const [errorActividad, setErrorActividad] = useState('')
  const [totalActividad, setTotalActividad] = useState(0)
  const [filtrosActividad, setFiltrosActividad] = useState({
    desde: '', hasta: '', usuario: '', modulo: '', accion: '',
  })

  // [RGM] 2026-10-16 (pedido Rafael): usuario como SELECT en los filtros
  // (Actividad y pestaña GX); opciones = usuarios de la empresa (mismo GET
  // /usuarios que UsuariosPage). El backend filtra EXACTO por código.
  const [usuarios, setUsuarios] = useState<any[]>([])
  useEffect(() => {
    api.get<any[]>(`/usuarios?empresa=${empresa}`).then(setUsuarios).catch(() => setUsuarios([]))
  }, [empresa])

  const opcionesUsuario = usuarios.map((u) => (
    <option key={u.usuarioCodigo} value={u.usuarioCodigo}>
      {u.usuarioNombre ? `${u.usuarioCodigo} — ${u.usuarioNombre}` : u.usuarioCodigo}
    </option>
  ))

  // ── Filtros de la pestaña GX (Auditoria) — el GET /auditoria ya soporta
  // desde/hasta/usuario (AuditoriaController.Consultar). ─────────────────
  const [filtrosGx, setFiltrosGx] = useState({ desde: '', hasta: '', usuario: '' })

  const cargarGx = (filtros = filtrosGx) => {
    setCargando(true)
    setError('')
    const qs = new URLSearchParams({ empresa })
    if (filtros.desde) qs.set('desde', filtros.desde)
    if (filtros.hasta) qs.set('hasta', filtros.hasta)
    if (filtros.usuario.trim()) qs.set('usuario', filtros.usuario.trim())
    api.get<any[]>(`/auditoria?${qs.toString()}`)
      .then(setRows)
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }

  const generarGx = (e: React.FormEvent) => {
    e.preventDefault()
    cargarGx()
  }

  // Consulta server-side del visor (filtros exactos; "hasta" incluye el día
  // completo). El botón Generar re-consulta con los filtros vigentes.
  const cargarActividad = (filtros = filtrosActividad) => {
    setCargandoActividad(true)
    setErrorActividad('')
    const qs = new URLSearchParams({ empresa })
    if (filtros.desde) qs.set('desde', filtros.desde)
    if (filtros.hasta) qs.set('hasta', filtros.hasta)
    if (filtros.usuario.trim()) qs.set('usuario', filtros.usuario.trim())
    if (filtros.modulo) qs.set('modulo', filtros.modulo)
    if (filtros.accion) qs.set('accion', filtros.accion)
    api.get<ActividadEvento[]>(`/auditoria/actividad?${qs.toString()}`)
      .then(setEventos)
      .catch((e: any) => setErrorActividad(e.message))
      .finally(() => setCargandoActividad(false))
  }

  useEffect(() => {
    // Carga inicial del visor con los filtros vacíos (el backend acota a la
    // empresa del claim y devuelve el mes en curso).
    cargarActividad()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const generarActividad = (e: React.FormEvent) => {
    e.preventDefault()
    cargarActividad()
  }

  // [RGM] 2026-10-16 (pedido Rafael): al tocar una entrada/salida en la
  // pestaña GX se salta a Actividad filtrada por ese usuario y la fecha de la
  // sesión → "qué hizo en esa sesión" (eventos crear/modificar/eliminar de
  // ese usuario ese día).
  const irAActividadDeSesion = (fila: any) => {
    const fecha = String(fila.auditoriaFecha ?? '').slice(0, 10)
    const filtros = {
      desde: fecha, hasta: fecha,
      usuario: String(fila.auditoriaUsuario ?? ''), modulo: '', accion: '',
    }
    setFiltrosActividad(filtros)
    setTabActiva('actividad')
    cargarActividad(filtros)
  }

  useEffect(() => {
    // Carga inicial de la pestaña GX (sin filtros).
    cargarGx({ desde: '', hasta: '', usuario: '' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa])

  const [totalReg, setTotalReg] = useState(rows.length)
  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>Auditoria</h4>
          <small className="text-body-secondary">
            {tabActiva === 'actividad'
              ? `${totalActividad} ${totalActividad === 1 ? 'evento' : 'eventos'}`
              : `${totalReg} ${totalReg === 1 ? 'registro' : 'registros'}`}
          </small>
        </CCol>
      </CRow>

      {/* [RGM] 2026-10-16 (aprobado Rafael — auditoría de actividad): la vista
          GX de Auditoria queda TAL CUAL en su pestaña; "Actividad" es el visor
          de la tabla nueva AuditoriaEvento (misma página, dos vistas). */}
      <CTabs activeItemKey={tabActiva} onChange={(clave) => setTabActiva(String(clave))}>
        <CTabList variant="tabs">
          <CTab itemKey="gx">Auditoria</CTab>
          <CTab itemKey="actividad">Actividad</CTab>
        </CTabList>

        <CTabPanel itemKey="gx" className="pt-3">
          {error && <CAlert color="danger">{error}</CAlert>}

          <WwGrid
            title="Auditoria"
            columns={columnas}
            rows={rows}
            onTotalChange={setTotalReg}
            loading={cargando}
            onRowClick={irAActividadDeSesion}
            filters={
              <CForm onSubmit={generarGx}>
                <CRow className="g-3 align-items-end">
                  <CCol md={2}>
                    <CFormLabel>Desde</CFormLabel>
                    <CFormInput
                      type="date"
                      value={filtrosGx.desde}
                      onChange={(e) => setFiltrosGx({ ...filtrosGx, desde: e.target.value })}
                    />
                  </CCol>
                  <CCol md={2}>
                    <CFormLabel>Hasta</CFormLabel>
                    <CFormInput
                      type="date"
                      value={filtrosGx.hasta}
                      onChange={(e) => setFiltrosGx({ ...filtrosGx, hasta: e.target.value })}
                    />
                  </CCol>
                  <CCol md={3}>
                    <CFormLabel>Usuario</CFormLabel>
                    <CFormSelect
                      value={filtrosGx.usuario}
                      onChange={(e) => setFiltrosGx({ ...filtrosGx, usuario: e.target.value })}
                    >
                      <option value="">Todos</option>
                      {opcionesUsuario}
                    </CFormSelect>
                  </CCol>
                  <CCol md="auto">
                    <CButton color="primary" type="submit" disabled={cargando}>
                      {cargando ? <CSpinner size="sm" /> : 'Generar'}
                    </CButton>
                  </CCol>
                </CRow>
              </CForm>
            }
          />
        </CTabPanel>

        <CTabPanel itemKey="actividad" className="pt-3">
          {errorActividad && <CAlert color="danger">{errorActividad}</CAlert>}

          <WwGrid
            title="Actividad"
            columns={columnasActividad}
            rows={eventos}
            loading={cargandoActividad}
            onTotalChange={setTotalActividad}
            busquedaPlaceholder="Filtrar actividad (usuario, entidad, detalle)..."
            filters={
              <CForm onSubmit={generarActividad}>
                <CRow className="g-3 align-items-end">
                  <CCol md={2}>
                    <CFormLabel>Desde</CFormLabel>
                    <CFormInput
                      type="date"
                      value={filtrosActividad.desde}
                      onChange={(e) => setFiltrosActividad({ ...filtrosActividad, desde: e.target.value })}
                    />
                  </CCol>
                  <CCol md={2}>
                    <CFormLabel>Hasta</CFormLabel>
                    <CFormInput
                      type="date"
                      value={filtrosActividad.hasta}
                      onChange={(e) => setFiltrosActividad({ ...filtrosActividad, hasta: e.target.value })}
                    />
                  </CCol>
                  <CCol md={2}>
                    <CFormLabel>Usuario</CFormLabel>
                    {/* El backend filtra EXACTO por código de usuario
                        (AuditoriaEventoService.cs:134-135). */}
                    <CFormSelect
                      value={filtrosActividad.usuario}
                      onChange={(e) => setFiltrosActividad({ ...filtrosActividad, usuario: e.target.value })}
                    >
                      <option value="">Todos</option>
                      {opcionesUsuario}
                    </CFormSelect>
                  </CCol>
                  <CCol md={2}>
                    <CFormLabel>Módulo</CFormLabel>
                    <CFormSelect
                      value={filtrosActividad.modulo}
                      onChange={(e) => setFiltrosActividad({ ...filtrosActividad, modulo: e.target.value })}
                    >
                      <option value="">Todos</option>
                      <option value="Egresos">Egresos</option>
                      <option value="Abonos">Abonos</option>
                      <option value="Facturas">Facturas</option>
                      <option value="Prestamos">Prestamos</option>
                      {/* [RGM] 2026-10-16 (Fase 2 aprobada Rafael): módulos
                          nuevos de la auditoría de actividad. */}
                      <option value="Usuarios">Usuarios</option>
                      <option value="Clientes">Clientes</option>
                      <option value="Rutas">Rutas</option>
                      <option value="Catalogos">Catalogos</option>
                    </CFormSelect>
                  </CCol>
                  <CCol md={2}>
                    <CFormLabel>Acción</CFormLabel>
                    <CFormSelect
                      value={filtrosActividad.accion}
                      onChange={(e) => setFiltrosActividad({ ...filtrosActividad, accion: e.target.value })}
                    >
                      <option value="">Todas</option>
                      <option value="CREAR">CREAR</option>
                      <option value="MODIFICAR">MODIFICAR</option>
                      <option value="ELIMINAR">ELIMINAR</option>
                    </CFormSelect>
                  </CCol>
                  <CCol md="auto">
                    <CButton color="primary" type="submit" disabled={cargandoActividad}>
                      {cargandoActividad ? <CSpinner size="sm" /> : 'Generar'}
                    </CButton>
                  </CCol>
                </CRow>
              </CForm>
            }
          />
        </CTabPanel>
      </CTabs>
    </>
  )
}

export default AuditoriaPage
